import { ELEVEN_LABS_LANGUAGES } from '@/components/Voice/voiceInfo';
import { useAllVoices } from '@/hooks/useAllVoices';
import {
    ttsVoiceAgeFilter,
    ttsVoiceFunctionFilter,
    ttsVoiceGenderFilter,
    ttsVoiceLanguageFilter,
    ttsVoiceProviderFilter,
    ttsVoiceSearchFilter,
    ttsVoiceTierFilter,
    ttsVoiceUseCaseFilter,
} from '@/state/presenters';
import { useUserSettings } from '@/state/user-settings/UserSettingsProvider';
import { isProPlus } from '@/utils/isProPlus';
import { AGE, FUNCTION, GENDER, TIER, USE_CASE } from '@d-id/tts';
import { IVoice, Providers, VoiceAccess } from '@d-id/types';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { isClonedVoice } from './useClonedVoices';

export const ageMap = {
    [AGE.YOUNG]: new Set(['young', 'youth']),
    [AGE.ADULT]: new Set(['middle-aged', 'middle aged', 'adult']),
    [AGE.OLDER]: new Set(['old', 'older']),
};

/**
 * For each voice from Elevenlabs, and Multilingual voice from Microsoft, add a "multilingual" voice to the list.
 * This is used for the "Multilingual" voice filter.
 */
export const withMultilingualVoices = (voices: IVoice[]): IVoice[] => {
    const multilingualVoiceMap = new Map<IVoice['id'], IVoice>();
    voices.forEach(voice => {
        const is11labs = voice.provider === Providers.Elevenlabs;
        const isMsMulti = isMicrosoftMultilingualVoice(voice);
        const isAzureOpenAiMulti = isAzureOpenAiMultilingualVoice(voice);

        if (!is11labs && !isMsMulti && !isAzureOpenAiMulti) return;

        multilingualVoiceMap.set(voice.id, { ...voice, language: 'multilingual' });
    });
    return [...voices, ...multilingualVoiceMap.values()];
};

export enum VoiceFiltersEnum {
    SEARCH = 'search',
    LANGUAGE = 'language',
    GENDER = 'gender',
    USE_CASE = 'useCase',
    TIER = 'tier',
    VOICE_FUNCTION = 'voiceFunction',
    AGE = 'age',
    PROVIDERS = 'providers',
    SOURCE = 'source',
    CUSTOM = 'custom',
}

export type VoiceFilters = {
    [VoiceFiltersEnum.SEARCH]: string;
    [VoiceFiltersEnum.LANGUAGE]: string;
    [VoiceFiltersEnum.GENDER]: GENDER | 'all';
    [VoiceFiltersEnum.USE_CASE]: 'all' | USE_CASE;
    [VoiceFiltersEnum.TIER]: 'all' | TIER;
    [VoiceFiltersEnum.VOICE_FUNCTION]: FUNCTION[];
    [VoiceFiltersEnum.AGE]: AGE | 'all';
    [VoiceFiltersEnum.PROVIDERS]: Providers[];
};

export type LocalVoiceFilters = {
    [VoiceFiltersEnum.PROVIDERS]?: Providers[];
    [VoiceFiltersEnum.SOURCE]?: 'default' | 'imported';
    [VoiceFiltersEnum.LANGUAGE]?: string;
    [VoiceFiltersEnum.CUSTOM]?: (voice: IVoice) => boolean;
};

export const cutOffDialect = (language: string) => language.replace(/\s*\(.*\)$/, '');

const isMicrosoftMultilingualVoice = (voice: IVoice) => {
    if (voice.provider !== Providers.Microsoft) return false;

    return voice.name.toLowerCase().includes('multilingual');
};

const isAzureOpenAiMultilingualVoice = (voice: IVoice) => {
    if (voice.provider !== Providers.AzureOpenAi) return false;

    return voice.name.toLowerCase().includes('multilingual');
};

export const isElevenLabsMultilingualVoice = (voice: IVoice) => {
    if (voice.provider !== Providers.Elevenlabs) return false;

    return (
        voice.language === 'multilingual' || // voice is multilingual
        voice.access === VoiceAccess.ExternalPrivate || // access external-private or privatemeans that the voice is a cloned voice so multilingual voice
        voice.access === VoiceAccess.Private ||
        voice.config?.modelId?.includes('multilingual')
    );
};

export const isMultilingualVoice = (voice: IVoice) => {
    return isElevenLabsMultilingualVoice(voice) || isMicrosoftMultilingualVoice(voice) || isAzureOpenAiMultilingualVoice(voice);
};

export const isVoiceSupportingLanguage = (voice: IVoice, language: string) => {
    if (language === 'all') return true;

    // shortest path check
    if (voice.language.toLowerCase() === language.toLowerCase()) {
        return true;
    }

    // for microsoft and azure openai multilingual voices
    if (isMicrosoftMultilingualVoice(voice) || isAzureOpenAiMultilingualVoice(voice)) {
        return true;
    }

    // for elevenlabs multilingual voices
    if (voice.provider === Providers.Elevenlabs) {
        if (language === 'multilingual') {
            const isLanguageAvailableIn11labs = ELEVEN_LABS_LANGUAGES.some(lang => lang.toLowerCase() === voice.language?.toLowerCase());

            return isLanguageAvailableIn11labs;
        }

        if (isElevenLabsMultilingualVoice(voice)) {
            const isLanguageAvailableIn11labs = ELEVEN_LABS_LANGUAGES.some(lang => lang.toLowerCase() === language.toLowerCase());
            const languageIsMultilingual = language.toLowerCase() === 'multilingual';

            return isLanguageAvailableIn11labs || languageIsMultilingual;
        }
    }

    // check for base language without dialect
    if (cutOffDialect(voice.language) === language) {
        return true;
    }

    return voice.locale.toLowerCase().includes(language.toLowerCase()) || voice.language.toLowerCase().includes(language.toLowerCase());
};

const languageFilter = (voice: IVoice, language: string | undefined) => {
    if (language === 'all') return true;

    if (language === 'multilingual') return voice.language === 'multilingual';

    if (!language) return voice.language.toLowerCase().includes('english');

    if (voice.provider === Providers.Microsoft && voice.language === 'multilingual') {
        return true;
    }

    if (voice.provider === Providers.AzureOpenAi && voice.language === 'multilingual') {
        return true;
    }

    return voice.locale.toLowerCase().includes(language.toLowerCase()) || voice.language.toLowerCase().includes(language.toLowerCase());
};

export const getVoiceFilterFunctions = (filters: VoiceFilters) => ({
    language: (voice: IVoice) => languageFilter(voice, filters.language),
    gender: (voice: IVoice) => filters.gender === 'all' || voice.gender.toLowerCase() === filters.gender.toLowerCase(),
    age: (voice: IVoice) => filters.age === 'all' || ageMap[filters.age]?.has(voice?.age ?? ''),
    useCase: (voice: IVoice) => filters.useCase === 'all' || voice.useCase?.toLowerCase() === filters.useCase.toLowerCase(),
    tier: (voice: IVoice) => {
        if (filters.tier === 'all') return true;
        const proPlus = isProPlus(voice);
        return (filters.tier === TIER.PREMIUM && proPlus) || (filters.tier === TIER.STANDARD && !proPlus);
    },
    voiceFunction: (voice: IVoice) =>
        !filters.voiceFunction.length ||
        (filters.voiceFunction.includes(FUNCTION.SPEED) && voice.provider !== Providers.Elevenlabs) ||
        (filters.voiceFunction.includes(FUNCTION.STYLE) && !!voice.styles.length),
    providers: (voice: IVoice) => !filters.providers.length || filters.providers.includes(voice.provider),
    search: (voice: IVoice) => !filters.search || voice.name.toLowerCase().includes(filters.search.toLowerCase()),
});

export const getLocalVoiceFilterFunctions = (filters?: LocalVoiceFilters) => ({
    source: (voice: IVoice) => {
        if (!filters?.source) return true;

        if (voice.voiceType === 'Injected') return true;

        const isImported = voice.access === VoiceAccess.ExternalPrivate || isClonedVoice(voice);

        if (filters.source === 'default') return !isImported;
        if (filters.source === 'imported') return isImported;

        return true;
    },
    custom: (voice: IVoice) => filters?.custom?.(voice) ?? true,
});

export const sortVoices = (voices: IVoice[], favoriteVoices: string[], injectedVoices: IVoice[] = [], languageFilter?: string): IVoice[] =>
    voices.sort((a, b) => {
        const aInjectedIndex = injectedVoices.findIndex(v => v.id === a.id);
        const bInjectedIndex = injectedVoices.findIndex(v => v.id === b.id);

        const aFavoriteIndex = favoriteVoices.findIndex(v => v === `${a.locale}-${a.id}`);
        const bFavoriteIndex = favoriteVoices.findIndex(v => v === `${b.locale}-${b.id}`);

        if (aInjectedIndex !== -1 && bInjectedIndex === -1) return -1;
        if (aInjectedIndex === -1 && bInjectedIndex !== -1) return 1;

        if (aInjectedIndex !== -1 && bInjectedIndex !== -1) return aInjectedIndex - bInjectedIndex;

        if (aFavoriteIndex !== -1 && bFavoriteIndex === -1) return -1;
        if (aFavoriteIndex === -1 && bFavoriteIndex !== -1) return 1;

        if (aFavoriteIndex !== -1 && bFavoriteIndex !== -1) return aFavoriteIndex - bFavoriteIndex;

        // If language filter is set and it's not "all" or "multilingual", show multilingual voices last
        if (languageFilter && languageFilter !== 'all' && languageFilter !== 'multilingual') {
            const isAMultilingual = a.language === 'multilingual';
            const isBMultilingual = b.language === 'multilingual';

            if (isAMultilingual && !isBMultilingual) return 1;
            if (isBMultilingual && !isAMultilingual) return -1;
        }

        return a.name.localeCompare(b.name);
    });

export const useFilteredVoices = (localFilters?: LocalVoiceFilters, injectedVoices: IVoice[] = []): IVoice[] => {
    const { data = [] } = useAllVoices();
    const { userSettings } = useUserSettings();

    const languageRecoil = useRecoilValue(ttsVoiceLanguageFilter);
    const languageLocal = localFilters?.language;
    const language = languageLocal || languageRecoil;

    const search = useRecoilValue(ttsVoiceSearchFilter);
    const gender = useRecoilValue(ttsVoiceGenderFilter);
    const useCase = useRecoilValue(ttsVoiceUseCaseFilter);
    const tier = useRecoilValue(ttsVoiceTierFilter);
    const voiceFunction = useRecoilValue(ttsVoiceFunctionFilter);
    const age = useRecoilValue(ttsVoiceAgeFilter);
    const providersRecoil = useRecoilValue(ttsVoiceProviderFilter);

    const providers = useMemo(() => {
        if (localFilters?.providers && localFilters.providers.length > 0) {
            if (providersRecoil.length === 0) {
                return [];
            }
            const localProvidersSet = new Set(localFilters.providers);
            const filtered = providersRecoil.filter(p => localProvidersSet.has(p));
            return filtered;
        }
        return providersRecoil;
    }, [localFilters?.providers, providersRecoil]);

    const multilingualVoices = useMemo(() => withMultilingualVoices(data), [data]);

    return useMemo(() => {
        const voicesMap = new Map<string, IVoice>();

        multilingualVoices.forEach(voice => {
            const key = `${voice.id}-${voice.language}`;
            voicesMap.set(key, voice);
        });

        injectedVoices.forEach(voice => {
            const key = `${voice.id}-${voice.language}`;
            voicesMap.set(key, voice);
        });

        const allVoices = Array.from(voicesMap.values());

        const filterFunctions = getVoiceFilterFunctions({ search, language, gender, useCase, tier, voiceFunction, age, providers });
        const localFilterFunctions = getLocalVoiceFilterFunctions(localFilters);

        const filters = Object.values(filterFunctions);

        // Apply the voice filters, from top to bottom
        const globallyFilteredVoices = allVoices.filter(voice => filters.every(fn => fn(voice)));
        const filteredVoices = localFilters
            ? globallyFilteredVoices.filter(voice => Object.values(localFilterFunctions).every(fn => fn(voice)))
            : globallyFilteredVoices;

        const favoriteVoices = userSettings?.preferences?.voices ?? [];
        const sortedVoices = sortVoices(filteredVoices, favoriteVoices, injectedVoices, language);

        return sortedVoices;
    }, [
        localFilters,
        injectedVoices,
        multilingualVoices,
        search,
        language,
        gender,
        useCase,
        tier,
        voiceFunction,
        age,
        providers,
        userSettings,
    ]);
};
